import {
  closestCenter,
  pointerWithin,
  rectIntersection,
  type Active,
  type CollisionDetection,
  type DroppableContainer,
} from '@dnd-kit/core';
import { isOptimisticCardId } from './cards';
import { neighboursForMove } from './list-drag';

/**
 * Chunk `C3`, agent B (`FE-12.3`). Signatures fixed by
 * `docs/design/c3/C3-BRIEF.md` §6.2.
 *
 * The geometry of a card drag. `lib/list-drag.ts` is the sibling and the
 * precedent:
 *
 *   - ✅ `reorderIds` and `neighboursForMove` are reused (only the latter,
 *     directly — see the "why not `reorderIds`" note on `cardNeighboursForDrop`
 *     below), because they are generic over IDS. Not copied.
 *   - ⚠ They are NOT generic over CONTAINERS — composing a move across two
 *     lists' arrays is this file's whole job, and `neighboursForMove` alone
 *     cannot do it because a cross-list `activeId` is never a member of the
 *     destination list's array, which is the one thing that function assumes.
 *   - ⚠ `STRIP_AUTO_SCROLL` is not imported. Its `threshold.y` is `0` because
 *     the strip never scrolls vertically; a card drag needs both axes.
 */

export const CARD_DRAG_TYPE = 'card';
export const LIST_DRAG_TYPE = 'list';

/** What a card's `useSortable({ data })` carries, so one `DndContext` can tell the two drags apart. */
export interface CardDragData {
  readonly type: typeof CARD_DRAG_TYPE;
  readonly listId: string;
}

/** The minimum shape of the board this file reads — deliberately not `BoardContents`, so it is testable with three lines of fixture. */
export interface DragList {
  readonly id: string;
  readonly cards: readonly { readonly id: string }[];
}

/** Reads `active.data.current`, defensively — dnd-kit types it as `unknown`-shaped `Data`. */
function dragDataType(active: { data: { current?: unknown } }): unknown {
  const data = active.data.current as { type?: unknown } | undefined;
  return data?.type;
}

export function isCardDrag(active: Active | null): boolean {
  if (!active) {
    return false;
  }
  return dragDataType(active) === CARD_DRAG_TYPE;
}

/**
 * The list a drop resolves to. A card droppable resolves to the list holding
 * that card; a column droppable resolves to itself — which is what makes an
 * **empty** list a drop target at all.
 */
export function resolveDropListId(
  overId: string | null,
  lists: readonly DragList[],
): string | null {
  if (overId === null) {
    return null;
  }

  const owningList = lists.find((list) => list.cards.some((card) => card.id === overId));
  if (owningList) {
    return owningList.id;
  }

  if (lists.some((list) => list.id === overId)) {
    return overId;
  }

  return null;
}

/**
 * Walks a candidate list of ids outward from `fromIndex` (inclusive of it in
 * the forward direction, exclusive in the backward one — mirroring
 * `neighboursForMove`'s own two loops), skipping any id in `excludeIds`.
 * Shared by the two branches of `cardNeighboursForDrop` below so the
 * "walk further out past a placeholder" rule is written once.
 */
function walkNeighbours(
  ids: readonly string[],
  insertAt: number,
  excludeIds: ReadonlySet<string>,
): { previousCardId: string | null; nextCardId: string | null } {
  let previousCardId: string | null = null;
  for (let i = insertAt - 1; i >= 0; i -= 1) {
    const id = ids[i]!;
    if (!excludeIds.has(id)) {
      previousCardId = id;
      break;
    }
  }

  let nextCardId: string | null = null;
  for (let i = insertAt; i < ids.length; i += 1) {
    const id = ids[i]!;
    if (!excludeIds.has(id)) {
      nextCardId = id;
      break;
    }
  }

  return { previousCardId, nextCardId };
}

/**
 * The two null-shaped outcomes (brief §3.2, §3.4), and they are NOT the same:
 *
 *   - returns `null`                                        → the drop is a
 *     no-op. SEND NOTHING.
 *   - returns `{ previousCardId: null, nextCardId: null }`   → the bottom of
 *     an EMPTY destination list (or, within one list, of a list whose only
 *     other cards are optimistic placeholders). A legal request, and it IS
 *     sent.
 *
 * `null` is returned in exactly one case: `activeId === overId`, the literal
 * "dropped back on itself" gesture. Every other input — including a drop that
 * lands the card back in the same slot by a different route — is answered
 * with a real (possibly both-null) pair and is sent; the server's byte-
 * identical response (§3.4) is the backstop for that, not this function's job.
 *
 * ⚠ Two composition paths, and they are NOT one function reused twice:
 *
 *   - **Same list.** This is exactly `list-drag.ts`'s problem shape — reorder
 *     one array — so it reuses `neighboursForMove` directly. The one thing it
 *     must correct for: `neighboursForMove` returns `null` both for the
 *     literal no-op AND for "no real card flanks the gap on either side",
 *     which (having already ruled out `activeId === overId` above) can only
 *     happen when `activeId` is the sole non-placeholder card in the list —
 *     i.e. it is already correctly positioned relative to every real card
 *     there is, which genuinely IS a no-op. It must also handle `overId`
 *     naming no card in the list at all: the column-droppable fallback
 *     (§7.6) for "dropped past the last card", which `neighboursForMove`
 *     would otherwise misread as an absent-id no-op and silently swallow —
 *     that is a real move to the bottom, not a no-op.
 *   - **Cross list.** `activeId` is not a member of the destination list's
 *     array at all, so `neighboursForMove`'s remove-and-reinsert machinery
 *     (built on `reorderIds`, which assumes both ids share one array) does
 *     not apply. This lands the card immediately BEFORE `overId` when it
 *     names a card in the destination, or at the bottom otherwise (an empty
 *     list, or a drop past the last card) — the composition this file exists
 *     for.
 *
 * ⚠ PRECONDITION, mirroring `neighboursForMove`'s own (`list-drag.ts`):
 * `lists` must be the board contents as rendered BEFORE this move is
 * applied, with `activeId` still listed under its SOURCE list. If a caller
 * has already spliced `activeId` into the destination list (e.g. an
 * `onDragOver` handler that reorders optimistically as the pointer moves)
 * before calling this, `sourceList` resolves to the destination, the
 * function takes the SAME-LIST branch instead of the cross-list one, and
 * `neighboursForMove` computes against an already-moved array — the exact
 * silent off-by-one its own docstring warns about, one slot further than
 * intended, with nothing red anywhere. Whoever wires this into `BoardFace`
 * (agent G) must call it against the pre-drag snapshot.
 */
export function cardNeighboursForDrop(args: {
  readonly lists: readonly DragList[];
  readonly activeId: string;
  readonly overId: string;
  readonly toListId: string;
}): {
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
} | null {
  const { lists, activeId, overId, toListId } = args;

  if (activeId === overId) {
    return null;
  }

  const destination = lists.find((list) => list.id === toListId);
  if (!destination) {
    return null;
  }

  const excludeIds = new Set(
    destination.cards
      .filter((card) => isOptimisticCardId(card.id))
      .map((card) => card.id),
  );

  const sourceList = lists.find((list) =>
    list.cards.some((card) => card.id === activeId),
  );
  const sameList = sourceList?.id === toListId;

  if (sameList) {
    const orderedIds = destination.cards.map((card) => card.id);

    if (orderedIds.includes(overId)) {
      const result = neighboursForMove(orderedIds, activeId, overId, {
        excludeIds: [...excludeIds],
      });
      // See the docstring: `null` here means "the only real card left is
      // `activeId` itself" (activeId === overId is already ruled out above),
      // which is a genuine no-op, not a swallowed both-null.
      if (result === null) {
        return null;
      }
      return { previousCardId: result.previousId, nextCardId: result.nextId };
    }

    // `overId` names no card in this list — the column-droppable fallback,
    // meaning "dropped past the last card". A real move to the bottom.
    const otherIds = orderedIds.filter((id) => id !== activeId);
    return walkNeighbours(otherIds, otherIds.length, excludeIds);
  }

  // Cross list: land immediately before `overId` when it is a card here,
  // otherwise at the bottom (empty destination, or past the last card).
  const otherIds = destination.cards.map((card) => card.id);
  const overIndex = otherIds.indexOf(overId);
  const insertAt = overIndex === -1 ? otherIds.length : overIndex;
  return walkNeighbours(otherIds, insertAt, excludeIds);
}

function dataType(container: DroppableContainer): unknown {
  const data = container.data.current as { type?: unknown } | undefined;
  return data?.type;
}

function isCardDroppable(container: DroppableContainer): boolean {
  return dataType(container) === CARD_DRAG_TYPE;
}

function droppableListId(container: DroppableContainer): unknown {
  const data = container.data.current as { listId?: unknown } | undefined;
  return data?.listId;
}

function firstHitContainer(
  hits: readonly { readonly id: string | number }[],
  droppableContainers: readonly DroppableContainer[],
): DroppableContainer | undefined {
  const first = hits[0];
  if (!first) {
    return undefined;
  }
  return droppableContainers.find((container) => container.id === first.id);
}

/**
 * ⚠ **One function, passed to `DndContext` once.** Do not swap the
 * `collisionDetection` prop on drag-type state — branch INSIDE here on
 * `active.data.current.type` (via `isCardDrag`).
 *
 * The board's droppable set is mixed: list columns, cards, and the columns
 * themselves as card containers. `closestCenter` alone over that set picks
 * silently wrong targets on every pointer move, with nothing red anywhere.
 *
 *   - a **list** drag: `closestCenter` over the column droppables only;
 *   - a **card** drag: `pointerWithin` → `rectIntersection` → `closestCenter`
 *     over the card droppables, falling back to the column droppable the
 *     pointer is inside, so an empty list is still a target.
 *
 * ⚠ **Why this is NOT "closestCenter over every card, then fall back if
 * empty"**: `closestCenter` never returns an empty result as long as at
 * least one card droppable exists ANYWHERE on the board — it has no distance
 * cutoff, it just sorts. So a naive "cards first, columns only if the card
 * chain is empty" waterfall would never actually reach the column fallback
 * on any board that has at least one card in it: a member dragging a card
 * over an EMPTY list, on a board where some OTHER list has cards, would
 * silently resolve to the nearest card in that OTHER list — exactly the
 * "silently wrong target" failure mode this function exists to prevent, and
 * it would pass a test that (like an earlier draft of this file's test suite)
 * only fed it a board with zero cards anywhere.
 *
 * So the pointer/rect phase runs over the FULL mixed droppable set first, to
 * find which COLUMN the pointer is over — a card's smaller rect naturally
 * sorts ahead of its enclosing column in `pointerWithin`'s corner-distance
 * sort, so this still resolves directly to a card when the pointer is over
 * one. Once the column is known, `closestCenter` is narrowed to just the
 * cards whose `data.current.listId` names that column (the reason
 * `CardDragData` carries `listId` at all) — which is what makes "nearest
 * card" mean nearest card IN THIS COLUMN, and what makes an empty column
 * resolve to itself rather than to a card three lists over.
 *
 * ⚠ **A non-empty column must ALSO be reachable as a target, past its own
 * last card** — `closestCenter` over "the cards in this column" always
 * returns one of them, with no distance cutoff, so narrowing alone can never
 * resolve to the column itself once it holds even one card. Before trusting
 * that narrowed `closestCenter`, this checks whether the pointer has moved
 * BELOW every card in the column (comparing against `droppableRects`, the
 * only place true geometry is available — `cardNeighboursForDrop` sees only
 * ids, which is why this check has to live here and not there). Below all of
 * them → the column itself, exactly as an empty column already resolves.
 *
 * ⚠ "column droppables" here means "every droppable that is NOT tagged
 * `CARD_DRAG_TYPE`" rather than a positive `LIST_DRAG_TYPE` check.
 * `SortableListColumn.tsx` (shipped by `C2`, not owned by any `C3` agent) does
 * not currently pass `data: { type: LIST_DRAG_TYPE }` to its own
 * `useSortable` call, so filtering *in* on that tag would leave the list
 * branch with zero droppables to collide against. Filtering *out* the card
 * droppables is equivalent on this board (its `DndContext` only ever holds
 * list-column and card droppables) and does not depend on that file being
 * touched. `LIST_DRAG_TYPE` is still exported — for `CardDragData`'s
 * counterpart and for whoever wires it up later — this file just does not
 * require it to be wired to work correctly today. Reported to the parent.
 */
export const boardCollisionDetection: CollisionDetection = (args) => {
  if (!isCardDrag(args.active)) {
    // A list drag: closestCenter over the column droppables only.
    const columnContainers = args.droppableContainers.filter(
      (container) => !isCardDroppable(container),
    );
    return closestCenter({ ...args, droppableContainers: columnContainers });
  }

  // A card drag. First, find what the pointer/rect is over across BOTH cards
  // and columns together, so an empty stretch of a column (below its last
  // card, or the whole of an empty list) can be told apart from "not over
  // this board at all".
  const pointerHits = pointerWithin(args);
  const boundaryHits = pointerHits.length > 0 ? pointerHits : rectIntersection(args);
  const target = firstHitContainer(boundaryHits, args.droppableContainers);

  if (target && isCardDroppable(target)) {
    // The pointer/rect is over a card directly — that already IS the
    // target. Re-running the pointer/rect chain scoped to cards alone would
    // repeat work `boundaryHits` already did; just drop any column entries
    // that happened to trail behind it in the same collision list.
    return boundaryHits.filter((hit) => {
      const container = args.droppableContainers.find((c) => c.id === hit.id);
      return container !== undefined && isCardDroppable(container);
    });
  }

  if (target) {
    // The pointer is over column space, not over any card.
    const cardsInThisColumn = args.droppableContainers.filter(
      (container) =>
        isCardDroppable(container) && droppableListId(container) === target.id,
    );

    if (cardsInThisColumn.length > 0) {
      const lowestCardBottom = Math.max(
        ...cardsInThisColumn.map(
          (container) =>
            args.droppableRects.get(container.id)?.bottom ?? Number.NEGATIVE_INFINITY,
        ),
      );
      const referenceY = args.collisionRect.top + args.collisionRect.height / 2;
      if (referenceY <= lowestCardBottom) {
        // Still above the bottom of the column's cards: the nearest one.
        return closestCenter({ ...args, droppableContainers: cardsInThisColumn });
      }
      // Past the last card: fall through to the column itself, below.
    }

    // An empty column, or past its last card either way: the column
    // droppable itself is the target (§7.6).
    return boundaryHits;
  }

  // Over no droppable at all (e.g. the gap between two columns).
  // `boundaryHits` being empty here means neither `pointerWithin` nor
  // `rectIntersection` found anything over the FULL set, so re-running
  // either one scoped to cards alone — a subset — could not find anything
  // either. Only `closestCenter`, which has no overlap requirement, can
  // still resolve something: the closest card anywhere, as the true last
  // resort.
  const cardContainers = args.droppableContainers.filter(isCardDroppable);
  return closestCenter({ ...args, droppableContainers: cardContainers });
};

/**
 * Both axes: the board scrolls left/right and a list scrolls up/down
 * (`FR-BOARD-025` AC3 and its behaviour sentence).
 *
 * ⚠ FINDING (brief §6.2.1): **one config serves both drags, verified against
 * dnd-kit's own source** (`@dnd-kit/core` 6.3.1, `core.esm.js`), not assumed.
 *
 * `useAutoScroller` walks a dragged node's `scrollableAncestors` and commits
 * to the FIRST container (in `TraversalOrder.TreeOrder` — outermost first)
 * for which `getScrollDirectionAndSpeed` computes a nonzero speed on either
 * axis. For a LIST drag, the only scrollable ancestor is the strip
 * (`#board-lists`, `overflow-x-auto overflow-y-hidden`) — `isScrollable`
 * flags it as scrollable because `overflowX` matches, but `getScrollableAncestors`
 * does not gate `getScrollDirectionAndSpeed`'s per-axis computation on which
 * specific `overflow*` property triggered inclusion. What DOES protect the Y
 * axis is `getScrollPosition`: the strip has no vertical overflow at all
 * (`scrollHeight === clientHeight`, both column headers and column bodies
 * being capped to the strip's own height), so `maxScroll.y === 0`, which makes
 * `isTop` and `isBottom` BOTH `true` regardless of pointer position — and
 * `getScrollDirectionAndSpeed`'s `!isTop`/`!isBottom` guards then block a
 * vertical scroll on that container unconditionally, independent of
 * `threshold.y`'s value. So `threshold.y > 0` has nothing to act on during a
 * list drag: the guard, not the threshold, is what keeps it inert.
 *
 * A CARD drag's dragged node has two scrollable ancestors — the card `<ol>`
 * (`overflow-y-auto`, nearest) and the strip (`overflow-x-auto`, outermost).
 * TreeOrder tries the strip first: it can supply X speed near a left/right
 * edge, but never Y (by the same `maxScroll.y === 0` argument), so a
 * vertical-edge approach correctly falls through to the card `<ol>`, which
 * can. Both axes are served by the SAME `{ x: 0.2, y: 0.2 }` without a second
 * `DndContext` or a state switch.
 *
 * `{ x: 0.2, y: 0.2 }` is dnd-kit's own `defaultThreshold`
 * (`core.esm.js`), and `10` is its own default `acceleration` — reused rather
 * than invented, the same choice `STRIP_AUTO_SCROLL` made.
 */
export const BOARD_AUTO_SCROLL: {
  readonly threshold: { readonly x: number; readonly y: number };
  readonly acceleration?: number;
} = { threshold: { x: 0.2, y: 0.2 }, acceleration: 10 };
